import React, { useCallback, useMemo, useRef, useState } from 'react';

import { getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { Player } from '@smart-tv/components/player/types';
import { LiveTvChannel } from '@smart-tv/typings/content';
import { isErrorDialogOpenAtom, isStreamingAtom, playerFocusKeyAtom } from '@smart-tv/atoms';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { uiStore } from '@smart-tv/components/program-sheet/_stores/store-ui';

/**
 * The props for the player context
 */
export interface PlayerContextProps {
  /**
   * The children that should be rendered inside the player context
   */
  children: React.ReactNode | React.ReactNode[];
}

/**
 * The values returned from the player context
 */
export interface PlayerContextValues {
  /**
   * The current instance of the player
   */
  player?: Player;
  playerNode?: HTMLDivElement;

  firstTimeLoading: boolean;
  onSetFirstTimeLoading: React.Dispatch<React.SetStateAction<boolean>>;

  settingsOverlay: boolean;
  toggleSettingsOverlay: () => void;

  playerRootElem: HTMLDivElement | undefined;
  setPlayerRootElem: (focusElement: HTMLDivElement) => void;

  onSetChannels: (channels: LiveTvChannel[]) => void;
  channels?: LiveTvChannel[];
  onSetPlayerFocusKey: (focusKey: string) => void;

  handleInit: (player: Player, playerNode: HTMLDivElement, focusKey?: string) => void;
  handleUnmount: () => void;

  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  isPlaying: boolean;

  isPlayingRef: React.MutableRefObject<boolean>;
}

/**
 * The player context.
 * It is used to control all the logic for the VisualOn player, and can be triggered throughout the entire app.
 */
export const PlayerContext = React.createContext<PlayerContextValues>({} as PlayerContextValues);

/**
 * The player context consumer
 */
export const PlayerConsumer = PlayerContext.Consumer;

/**
 * The player context provider
 */
export const PlayerProvider = (props: PlayerContextProps): JSX.Element => {
  const { children } = props;

  const setIsPlayingAtom = useSetAtom(isStreamingAtom);

  const isErrorDialogOpen = useAtomValue(isErrorDialogOpenAtom);
  // uses store instead of atomValue to avoid unnecessary re-renders.
  const store = useStore();
  const setPlayerFocusKeyAtomValue = useSetAtom(playerFocusKeyAtom);
  const [player, setPlayer] = useState<Player>();
  const [playerNode, setPlayerNode] = useState<HTMLDivElement>();

  const [playerRootElem, setPlayerRootElem] = useState<HTMLDivElement>();

  const [firstTimeLoading, setFirstTimeLoading] = useState(true);
  const [settingsOverlay, setSettingsOverlay] = useState(false);

  const [channels, setChannels] = useState<LiveTvChannel[]>();

  const [isPlaying, setIsPlaying] = useState(false);

  const isPlayingRef = React.useRef<boolean>(false);

  const handleSetPlayerFocusKey = useCallback((focusKey: string) => {
    setPlayerFocusKeyAtomValue(focusKey);
  }, []);

  const handleSettingsOverlay = useCallback(() => {
    setSettingsOverlay(!settingsOverlay);
    playerRootElem?.focus();
  }, [settingsOverlay, playerRootElem]);

  React.useEffect(() => {
    if (playerRootElem && !isErrorDialogOpen) {
      playerRootElem.focus();
    }
  }, [playerRootElem, isErrorDialogOpen]);

  const handleSetPlayerRootElem = useCallback((rootElem: HTMLDivElement) => {
    setPlayerRootElem(rootElem);
  }, []);

  const handleSetChannels = useCallback((channels: LiveTvChannel[]) => {
    setChannels(channels);
  }, []);

  const handleInit = useCallback(
    (player: Player, playerNode: HTMLDivElement) => {
      setPlayer(player);
      setPlayerNode(playerNode);
      setIsPlaying(true);
      setIsPlayingAtom(true);
      isPlayingRef.current = true;

      const playerFocusKey = store.get(playerFocusKeyAtom);
      if (playerFocusKey === null || playerFocusKey === '') {
        setPlayerFocusKeyAtomValue(getCurrentFocusKey());
      }
    },
    [store, setPlayerFocusKeyAtomValue, setIsPlayingAtom]
  );

  const handleUnmount = useCallback(() => {
    setPlayer(undefined);
    setPlayerNode(undefined);
    setIsPlaying(false);
    setIsPlayingAtom(false);
    isPlayingRef.current = false;
    setFirstTimeLoading(true);

    const playerFocusKey = store.get(playerFocusKeyAtom);
    if (playerFocusKey && !store.get(isErrorDialogOpenAtom)) {
      !uiStore.toggles.recordingDialog && setFocus(playerFocusKey);
      setPlayerFocusKeyAtomValue('');
    }
  }, [store, setPlayerFocusKeyAtomValue, setIsPlayingAtom]);

  const contextValues = useMemo(
    () => ({
      player,
      playerNode,
      handleInit,
      handleUnmount,
      settingsOverlay,
      firstTimeLoading,
      onSetFirstTimeLoading: setFirstTimeLoading,
      toggleSettingsOverlay: handleSettingsOverlay,
      playerRootElem: playerRootElem,
      setPlayerRootElem: handleSetPlayerRootElem,
      onSetChannels: handleSetChannels,
      channels,
      onSetPlayerFocusKey: handleSetPlayerFocusKey,
      setIsPlaying,
      isPlaying,
      isPlayingRef
    }),
    [
      player,
      playerNode,
      handleInit,
      handleUnmount,
      settingsOverlay,
      firstTimeLoading,
      handleSettingsOverlay,
      playerRootElem,
      handleSetPlayerRootElem,
      handleSetChannels,
      channels,
      handleSetPlayerFocusKey,
      isPlaying,
      isPlayingRef
    ]
  );

  return <PlayerContext.Provider value={contextValues}>{children}</PlayerContext.Provider>;
};

/**
 * A hook that is used to easily consume the player context.
 */
export const usePlayerContext = (): PlayerContextValues => {
  const values = React.useContext(PlayerContext);

  return { ...values };
};

export default PlayerContext;
